import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, TableRow as MuiTableRow } from '@mui/material';
import type {
  DataTableBaseColumn,
  DataTableRow,
  DataTableSortableColumn,
  DataTableSortStateProps,
  DataTableVariant,
  DataTableOnColumnOrderChangeClbk,
} from './TableTypes';
import { DraggableTableHeadColumn } from './DraggableTableHeadColumn';
import { colors } from '@sweep-io/sweep-design';
import { useState } from 'react';
import { DRAGGABLE_TABLE_OVERLAY_COLOR, stickyHeaderStyles } from './utils';

interface DraggableTableHeadProps<TRow extends DataTableRow> {
  columns: DataTableSortableColumn<TRow>[] | DataTableBaseColumn[];
  stickyHeader?: boolean;
  variant: DataTableVariant;
  sortState?: DataTableSortStateProps;
  sort: (columnName: string) => void;
  onColumnOrderChange?: DataTableOnColumnOrderChangeClbk;
  onDragOver?: (columnId: string | null) => void;
  onDragStart?: (columnId: string) => void;
  onDragEnd?: () => void;
  overColumnId?: string | null;
  setOverColumnId?: (columnId: string | null) => void;
  draggedColumnId: string | null;
  setDraggedColumnId: (columnId: string | null) => void;
  isColumnResizeAllowed?: boolean;
}

export function DraggableTableHead<TRow extends DataTableRow = any>({
  columns,
  stickyHeader,
  variant,
  sortState,
  sort,
  onColumnOrderChange,
  onDragOver,
  onDragStart,
  onDragEnd,
  overColumnId,
  setOverColumnId,
  draggedColumnId,
  setDraggedColumnId,
  isColumnResizeAllowed,
}: DraggableTableHeadProps<TRow>) {
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const draggedColumn = columns.find((col) => col.field === draggedColumnId);
  const [overlayWidth, setOverlayWidth] = useState<number | undefined>(undefined);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setDraggedColumnId(active.id as string);
    onDragStart?.(active.id as string);
    // Measure the width of the dragged column header
    const cell = document.querySelector(`[data-column-id="${active.id}"]`) as HTMLElement | null;
    if (cell) {
      setOverlayWidth(cell.offsetWidth);
    } else {
      setOverlayWidth(undefined);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const sourceIndex = columns.findIndex((col) => col.field === active.id);
      const destinationIndex = columns.findIndex((col) => col.field === over.id);
      onColumnOrderChange?.({ sourceIndex, destinationIndex });
    }
    setDraggedColumnId(null);
    setOverColumnId?.(null);
    onDragOver?.(null);
    onDragEnd?.();
    setOverlayWidth(undefined);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverColumnId?.(over ? (over.id as string) : null);
    onDragOver?.(over ? (over.id as string) : null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
    >
      <SortableContext
        items={columns.map((col) => col.field)}
        strategy={horizontalListSortingStrategy}
      >
        <MuiTableRow>
          {columns.map((column, index) => (
            <DraggableTableHeadColumn
              key={column.field}
              column={column}
              stickyHeader={stickyHeader}
              variant={variant}
              sortState={sortState}
              sort={sort}
              isDragging={draggedColumnId === column.field}
              isOver={overColumnId === column.field}
              isLastColumn={index === columns.length - 1}
              isColumnResizeAllowed={isColumnResizeAllowed}
            />
          ))}
          <Box
            component="td"
            sx={{
              p: 0,
              backgroundColor: `${colors.grey[100]} !important`,
              ...(stickyHeader ? { ...stickyHeaderStyles } : {}),
            }}
            key="action-column"
          />
        </MuiTableRow>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {draggedColumn && (
          <Box
            sx={{
              position: 'relative',
              backgroundColor: DRAGGABLE_TABLE_OVERLAY_COLOR,
              width: overlayWidth,
              borderLeft: `1px solid ${colors.grey[120]}`,
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
              zIndex: 1100,
              '& .MuiTableCell-root': {
                backgroundColor: `${colors.grey[50]} !important`,
              },
            }}
          >
            <MuiTableRow>
              <DraggableTableHeadColumn
                column={draggedColumn}
                stickyHeader={stickyHeader}
                variant={variant}
                sortState={sortState}
                sort={sort}
                isDragging={true}
                isOverlay={true}
                overlayWidth={overlayWidth}
                isColumnResizeAllowed={isColumnResizeAllowed}
              />
            </MuiTableRow>
          </Box>
        )}
      </DragOverlay>
    </DndContext>
  );
}
